import React, { useContext, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import Navbar from '../Items/Navbar';
import AuthContext from '../shared/AuthContext';
import { useTeamContext } from '../../context/TeamContext';
import TeamMemberGuideBanner from './TeamMemberGuideBanner';
import TeamGlobalRequestsPanel from './TeamGlobalRequestsPanel';
import PlanTierLabel from '../billing/PlanTierLabel';
import { FEATURE } from '../../config/planFeatures';
import { useUserPlan } from '../../hooks/useUserPlan';
import './teams.css';

function TeamSwitcher() {
  const { organizations, activeOrgId, setActiveOrgId } = useTeamContext();
  if (organizations.length <= 1) return null;
  return (
    <label className="teams-switcher">
      <span className="teams-sr-only">Workspace</span>
      <select
        value={activeOrgId ?? ''}
        onChange={(e) => setActiveOrgId(Number(e.target.value))}
      >
        {organizations.map((o) => (
          <option key={o.id} value={o.id}>
            {o.displayName || o.name}
          </option>
        ))}
      </select>
    </label>
  );
}

export default function TeamsLayout() {
  const { user } = useContext(AuthContext);
  const userProfile = user || JSON.parse(localStorage.getItem('userProfile') || 'null');
  const { orgId } = useParams();
  const { activeOrg, setActiveOrgId, pendingRequestCount, refreshPendingInbox } = useTeamContext();
  const navigate = useNavigate();
  const { canUseFeature } = useUserPlan();
  const teamLeaderboardLocked = !canUseFeature(FEATURE.TEAM_LEADERBOARD_ANALYTICS);

  React.useEffect(() => {
    refreshPendingInbox?.().catch(() => {});
  }, [refreshPendingInbox, orgId]);

  React.useEffect(() => {
    if (orgId) {
      setActiveOrgId(Number(orgId));
    }
  }, [orgId, setActiveOrgId]);

  const base = orgId ? `/teams/${orgId}` : '/teams';

  const workspaceTitle = activeOrg?.displayName || activeOrg?.name || 'Team workspace';
  const workspaceSubtitle = 'Collaborative learning — shared progress, assignments, and leaderboards.';

  return (
    <div className="teams-shell">
      <Navbar userProfile={userProfile} loading={false} />
      <div className="teams-page">
        {orgId && (
          <header className="teams-head">
            <div>
              <h1>{workspaceTitle}</h1>
              <p className="teams-sub">{workspaceSubtitle}</p>
            </div>
            <TeamSwitcher />
          </header>
        )}

        {orgId && <TeamMemberGuideBanner />}

        {orgId && pendingRequestCount > 0 && (
          <TeamGlobalRequestsPanel compact />
        )}

        {orgId && (
          <nav className="teams-nav" aria-label="Team workspace">
            <NavLink to={base} end className={({ isActive }) => (isActive ? 'is-active' : undefined)}>Overview</NavLink>
            <NavLink to={`${base}/members`} className={({ isActive }) => (isActive ? 'is-active' : undefined)}>Members</NavLink>
            <NavLink to={`${base}/requests`} className={({ isActive }) => (isActive ? 'is-active' : undefined)}>
              Requests
              {pendingRequestCount > 0 && <span className="teams-nav-badge">{pendingRequestCount}</span>}
            </NavLink>
            <NavLink to={`${base}/assignments`} className={({ isActive }) => (isActive ? 'is-active' : undefined)}>Assignments</NavLink>
            <NavLink to={`${base}/challenges`} className={({ isActive }) => (isActive ? 'is-active' : undefined)}>Challenges</NavLink>
            <NavLink
              to={`${base}/leaderboard`}
              className={({ isActive }) => (isActive ? 'is-active' : undefined)}
              onClick={(e) => {
                if (teamLeaderboardLocked) {
                  e.preventDefault();
                  navigate(`${base}/leaderboard`);
                }
              }}
            >
              Leaderboard
              {teamLeaderboardLocked && <PlanTierLabel tier="Team" className="teams-nav-plan-label" />}
            </NavLink>
            <NavLink to={`${base}/groups`} className={({ isActive }) => (isActive ? 'is-active' : undefined)}>Groups</NavLink>
            <NavLink to={`${base}/audit`} className={({ isActive }) => (isActive ? 'is-active' : undefined)}>Audit</NavLink>
            <NavLink to={`${base}/settings`} className={({ isActive }) => (isActive ? 'is-active' : undefined)}>Settings</NavLink>
          </nav>
        )}

        <Outlet />
      </div>
    </div>
  );
}
